import { useState, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Tag, Plus, X, Link2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { useQueryClient } from '@tanstack/react-query';
import { useIssueReleases, useReleases, releaseKeys } from '../../hooks/useReleases';
import * as releaseApi from '../../services/releaseService';
import SingleSelectDropdown from '../common/SingleSelectDropdown';

interface Props {
  projectId: string;
  issueId: string;
  hasChildren: boolean;
}

const STATUS_CONFIG: Record<string, { label: string; bg: string; text: string }> = {
  PLANNING: { label: '계획', bg: 'bg-gray-100', text: 'text-gray-700' },
  IN_PROGRESS: { label: '진행 중', bg: 'bg-blue-100', text: 'text-blue-700' },
  TESTING: { label: '테스트', bg: 'bg-amber-100', text: 'text-amber-700' },
  READY: { label: '준비 완료', bg: 'bg-green-100', text: 'text-green-700' },
  RELEASED: { label: '릴리즈됨', bg: 'bg-purple-100', text: 'text-purple-700' },
  CANCELLED: { label: '취소', bg: 'bg-red-100', text: 'text-red-700' },
};

export default function IssueReleaseSection({ projectId, issueId, hasChildren }: Props) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { key: projectKey } = useParams<{ key: string }>();
  const { data: linked, isLoading } = useIssueReleases(projectId, issueId);
  const { data: allReleases } = useReleases(projectId);

  const goToRelease = (releaseId: string) => {
    if (!projectKey) return;
    navigate(`/projects/${projectKey}/releases?id=${releaseId}`);
  };

  const [showPicker, setShowPicker] = useState(false);
  const [pickedReleaseId, setPickedReleaseId] = useState<string>('');
  const [pendingReleaseId, setPendingReleaseId] = useState<string | null>(null); // 자식 포함 여부 모달용
  const [busy, setBusy] = useState(false);

  const linkedIds = useMemo(() => new Set((linked ?? []).map((r) => r.releaseId)), [linked]);

  const availableReleases = useMemo(
    () => (allReleases ?? []).filter((r) => !linkedIds.has(r.releaseId)),
    [allReleases, linkedIds],
  );

  const invalidateAll = (releaseId: string, broad?: boolean) => {
    if (broad) {
      // 하위 작업 일괄 연결 시 — 후손 이슈들의 by-issue 캐시도 함께 갱신
      qc.invalidateQueries({ queryKey: ['releases', 'by-issue', projectId] });
    } else {
      qc.invalidateQueries({ queryKey: releaseKeys.byIssue(projectId, issueId) });
    }
    qc.invalidateQueries({ queryKey: releaseKeys.detail(projectId, releaseId) });
    qc.invalidateQueries({ queryKey: releaseKeys.list(projectId) });
  };

  const performAdd = async (releaseId: string, includeDescendants: boolean) => {
    setBusy(true);
    try {
      await releaseApi.addReleaseIssues(projectId, releaseId, [issueId], { includeDescendants });
      invalidateAll(releaseId, includeDescendants);
      toast.success(includeDescendants ? '릴리즈가 하위 항목 포함하여 연결되었습니다.' : '릴리즈가 연결되었습니다.');
      setPendingReleaseId(null);
      setShowPicker(false);
      setPickedReleaseId('');
    } catch {
      toast.error('릴리즈 연결에 실패했습니다.');
    } finally {
      setBusy(false);
    }
  };

  const handleSubmitPick = () => {
    if (!pickedReleaseId) return;
    if (hasChildren) {
      // 하위 포함 여부 확인 모달로 전환
      setPendingReleaseId(pickedReleaseId);
    } else {
      void performAdd(pickedReleaseId, false);
    }
  };

  const handleRemove = async (releaseId: string, version: string) => {
    if (!confirm(`'${version}' 릴리즈 연결을 해제하시겠습니까?`)) return;
    try {
      await releaseApi.removeReleaseIssue(projectId, releaseId, issueId);
      invalidateAll(releaseId);
      toast.success('릴리즈 연결이 해제되었습니다.');
    } catch {
      toast.error('연결 해제에 실패했습니다.');
    }
  };

  return (
    <div className="border-t border-gray-100 px-5 py-3">
      <div className="flex items-center justify-between mb-3">
        <h4 className="flex items-center gap-1.5 text-sm font-semibold text-gray-400 uppercase tracking-wider">
          <Tag size={12} />
          릴리즈
        </h4>
        <button
          onClick={() => setShowPicker(true)}
          className="inline-flex items-center gap-1 px-2 py-1 text-sm text-primary-600 hover:bg-primary-50 rounded transition-colors"
        >
          <Plus size={12} />
          연결
        </button>
      </div>

      {isLoading ? (
        <p className="text-sm text-gray-400">불러오는 중...</p>
      ) : (linked ?? []).length === 0 ? (
        <p className="text-sm text-gray-400">연결된 릴리즈가 없습니다.</p>
      ) : (
        <div className="space-y-1.5">
          {(linked ?? []).map((r) => {
            const sc = STATUS_CONFIG[r.status] ?? STATUS_CONFIG.PLANNING;
            return (
              <div
                key={r.releaseId}
                className="flex items-center gap-2 px-3 py-2 bg-gray-50 border border-gray-100 rounded-md group hover:bg-gray-100 hover:border-gray-200 transition-colors cursor-pointer"
                onClick={() => goToRelease(r.releaseId)}
              >
                <Link2 size={12} className="text-gray-400 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="text-sm font-medium text-gray-900 hover:underline">{r.version}</span>
                    <span className="text-sm text-gray-500 truncate">{r.title}</span>
                  </div>
                </div>
                <span className={`shrink-0 px-2 py-0.5 text-[11px] font-medium rounded ${sc.bg} ${sc.text}`}>
                  {sc.label}
                </span>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemove(r.releaseId, r.version);
                  }}
                  className="shrink-0 p-1 text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                  title="연결 해제"
                >
                  <X size={12} />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* 릴리즈 선택 모달 */}
      {showPicker && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
          onClick={(e) => {
            if (e.target === e.currentTarget && !busy) {
              setShowPicker(false);
              setPickedReleaseId('');
            }
          }}
        >
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <h2 className="text-base font-semibold text-gray-900">릴리즈 연결</h2>
              <button
                onClick={() => { setShowPicker(false); setPickedReleaseId(''); }}
                disabled={busy}
                className="p-1 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-md disabled:opacity-50"
              >
                <X size={18} />
              </button>
            </div>
            <div className="px-5 py-4 space-y-3">
              <label className="block text-sm font-medium text-gray-700">연결할 릴리즈</label>
              {availableReleases.length === 0 ? (
                <p className="text-sm text-gray-400">연결 가능한 릴리즈가 없습니다.</p>
              ) : (
                <SingleSelectDropdown
                  label="릴리즈 선택"
                  value={pickedReleaseId}
                  onChange={setPickedReleaseId}
                  clearable={false}
                  className="w-full"
                  options={availableReleases.map((r) => ({
                    value: r.releaseId,
                    label: `${r.version} — ${r.title}`,
                  }))}
                />
              )}
            </div>
            <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-gray-100">
              <button
                onClick={() => { setShowPicker(false); setPickedReleaseId(''); }}
                disabled={busy}
                className="px-3 py-1.5 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                취소
              </button>
              <button
                onClick={handleSubmitPick}
                disabled={!pickedReleaseId || busy}
                className="px-4 py-1.5 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50"
              >
                연결
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 하위 포함 여부 확인 모달 */}
      {pendingReleaseId && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40"
          onClick={(e) => {
            if (e.target === e.currentTarget && !busy) setPendingReleaseId(null);
          }}
        >
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4">
            <div className="px-5 py-4 border-b border-gray-100">
              <h2 className="text-base font-semibold text-gray-900">하위 작업에도 같은 릴리즈를 추가할까요?</h2>
            </div>
            <div className="px-5 py-4">
              <p className="text-sm text-gray-600">
                이 작업에는 하위 항목이 있습니다. 하위 항목과 그 하위 항목 전체에도 동일한 릴리즈를 함께 연결할 수 있습니다.
              </p>
            </div>
            <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-gray-100">
              <button
                onClick={() => setPendingReleaseId(null)}
                disabled={busy}
                className="px-3 py-1.5 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                취소
              </button>
              <button
                onClick={() => performAdd(pendingReleaseId, false)}
                disabled={busy}
                className="px-3 py-1.5 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                현재 작업만 추가
              </button>
              <button
                onClick={() => performAdd(pendingReleaseId, true)}
                disabled={busy}
                className="px-4 py-1.5 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50"
              >
                {busy ? '연결 중...' : '확인'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
